"use client";

import React, { useState, useRef, useCallback, useEffect } from "react";

interface Position {
  x: number;
  y: number;
}

export default function CoverPhoto() {
  const [position, setPosition] = useState<Position>({ x: 50, y: 50 });
  const [zoom, setZoom] = useState(1);
  const [isEditing, setIsEditing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ mx: number; my: number; px: number; py: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isEditing) return;
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ mx: e.clientX, my: e.clientY, px: position.x, py: position.y });
    },
    [isEditing, position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !dragStart || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((e.clientX - dragStart.mx) / rect.width) * 100;
      const dy = ((e.clientY - dragStart.my) / rect.height) * 100;
      // Clamp movement based on zoom level
      const maxOffset = ((zoom - 1) / zoom) * 50;
      setPosition({
        x: Math.max(50 - maxOffset, Math.min(50 + maxOffset, dragStart.px - dx)),
        y: Math.max(50 - maxOffset, Math.min(50 + maxOffset, dragStart.py - dy)),
      });
    },
    [isDragging, dragStart, zoom]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  // Touch support
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!isEditing) return;
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ mx: touch.clientX, my: touch.clientY, px: position.x, py: position.y });
    },
    [isEditing, position]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging || !dragStart || !containerRef.current) return;
      e.preventDefault();
      const touch = e.touches[0];
      const rect = containerRef.current.getBoundingClientRect();
      const dx = ((touch.clientX - dragStart.mx) / rect.width) * 100;
      const dy = ((touch.clientY - dragStart.my) / rect.height) * 100;
      const maxOffset = ((zoom - 1) / zoom) * 50;
      setPosition({
        x: Math.max(50 - maxOffset, Math.min(50 + maxOffset, dragStart.px - dx)),
        y: Math.max(50 - maxOffset, Math.min(50 + maxOffset, dragStart.py - dy)),
      });
    },
    [isDragging, dragStart, zoom]
  );

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  // When zoom changes, re-clamp position
  useEffect(() => {
    const maxOffset = ((zoom - 1) / zoom) * 50;
    setPosition((prev) => ({
      x: Math.max(50 - maxOffset, Math.min(50 + maxOffset, prev.x)),
      y: Math.max(50 - maxOffset, Math.min(50 + maxOffset, prev.y)),
    }));
  }, [zoom]);

  return (
    <div className="relative w-full" style={{ height: "220px" }}>
      {/* Image container */}
      <div
        ref={containerRef}
        className="w-full h-full overflow-hidden"
        style={{ cursor: isEditing ? (isDragging ? "grabbing" : "grab") : "default" }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <img
          src="/assets/images/ChatGPT_Image_Feb_26_2026_05_21_22_PM-1772101192068.png"
          alt="One Stop Advertising Infrastructure - Scale What Feels Unscalable with platform icons"
          draggable={false}
          style={{
            width: `${zoom * 100}%`,
            height: `${zoom * 100}%`,
            objectFit: "cover",
            objectPosition: `${position.x}% ${position.y}%`,
            display: "block",
            userSelect: "none",
            pointerEvents: "none",
            transition: isDragging ? "none" : "width 0.2s, height 0.2s",
          }}
        />
      </div>

      {/* Bottom gradient fade */}
      <div
        className="absolute bottom-0 left-0 right-0 pointer-events-none"
        style={{
          height: "60px",
          background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.95))",
        }}
      />

      {/* Edit button (top-right) */}
      {/* Adjust button removed */}

      {/* Editing controls overlay */}
      {isEditing && (
        <div
          className="absolute inset-x-0 bottom-0 flex flex-col gap-2 px-4 pb-3 pt-2"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
        >
          {/* Drag hint */}
          <p className="text-white text-center text-xs opacity-80 select-none">
            Drag image to reposition
          </p>

          {/* Zoom slider row */}
          <div className="flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white" className="w-4 h-4 flex-shrink-0 opacity-70">
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
            </svg>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 h-1.5 rounded-full accent-white"
              aria-label="Zoom cover photo"
            />
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white" className="w-5 h-5 flex-shrink-0 opacity-70">
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
            </svg>
          </div>

          {/* Done button */}
          <button
            onClick={() => setIsEditing(false)}
            className="self-center px-6 py-1.5 rounded-full text-black text-xs font-bold shadow"
            style={{ background: "#fff" }}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}
